import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
  HttpContextToken,
  HttpErrorResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';
import { AUTH_CONFIG } from '../auth.config';
import { AuthEventsService } from '@olv/ui-shared/services/auth-events.service';
import { TokenService } from '@olv/ui-shared/services/token.service';

export const HANDLED_FORBIDDEN = new HttpContextToken<((error: HttpErrorResponse) => boolean) | null>(() => null);

let isRefreshing = false;
let sessionEpoch = 0;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const cfg = inject(AUTH_CONFIG);

  const tokens = inject(TokenService);
  const authEvents = inject(AuthEventsService);

  // Also invalidates any in-flight refresh
  const endSession = (): void => {
    sessionEpoch++;
    isRefreshing = false;
    refreshTokenSubject.next(null);
    tokens.clear();
    authEvents.requestLogout(true);
  };

  const authBase = `${cfg.baseUrl}${cfg.endpoints.auth.base}`;
  const loginUrl = `${authBase}${cfg.endpoints.auth.login}`;
  const refreshUrl = `${authBase}${cfg.endpoints.auth.refresh}`;
  const mfaStatusUrl = `${authBase}${cfg.endpoints.auth.mfaStatus}`;

  const absolute = /^https?:\/\//i.test(req.url);
  const allowedPrefixes = new Set<string>([cfg.baseUrl, ...(cfg.authHeaderAllowedList ?? [])]);
  const shouldAttachAuthHeader = !absolute || Array.from(allowedPrefixes).some((p) => req.url.startsWith(p));

  const isPublicAuthCall =
    req.url.startsWith(loginUrl) || req.url.startsWith(refreshUrl) || req.url.startsWith(mfaStatusUrl);

  const accessToken = tokens.getAccessToken();
  const authedReq =
    accessToken && shouldAttachAuthHeader && !isPublicAuthCall
      ? req.clone({ setHeaders: { Authorization: `Bearer ${accessToken}` } })
      : req;

  return next(authedReq).pipe(
    catchError((error) => {
      if (error?.status === 403) {
        if (req.context.get(HANDLED_FORBIDDEN)?.(error)) {
          return throwError(() => error);
        }

        endSession();
        return throwError(() => error);
      }

      if (error?.status !== 401 || isPublicAuthCall) {
        return throwError(() => error);
      }

      if (!isRefreshing) {
        isRefreshing = true;
        refreshTokenSubject.next(null);

        const refreshToken = tokens.getRefreshToken();
        if (!refreshToken) {
          endSession();
          return throwError(() => error);
        }

        const epoch = sessionEpoch;
        return tokens.refresh().pipe(
          // Refresh cancelled before it settled
          finalize(() => {
            if (isRefreshing && epoch === sessionEpoch) {
              isRefreshing = false;
              refreshTokenSubject.next(null);
            }
          }),
          catchError((refreshError) => {
            if (epoch === sessionEpoch) {
              endSession();
            }
            return throwError(() => refreshError);
          }),
          switchMap((response) => {
            // Session ended during the refresh
            if (epoch !== sessionEpoch) {
              return throwError(() => error);
            }

            tokens.setTokens(response);
            isRefreshing = false;
            refreshTokenSubject.next(response.access_token);

            const retryReq = req.clone({
              setHeaders: {
                Authorization: `Bearer ${response.access_token}`,
              },
            });
            return next(retryReq).pipe(
              catchError((retryError) => {
                endSession();
                return throwError(() => retryError);
              }),
            );
          }),
        );
      }

      return refreshTokenSubject.pipe(
        filter((token) => token !== null || !isRefreshing),
        take(1),
        switchMap((newToken) => {
          // Refresh failed and already logged out
          if (newToken === null) {
            return throwError(() => error);
          }

          const retryReq = req.clone({
            setHeaders: {
              Authorization: `Bearer ${newToken}`,
            },
          });
          return next(retryReq).pipe(
            catchError((waitError) => {
              endSession();
              return throwError(() => waitError);
            }),
          );
        }),
      );
    }),
  );
};
